import React, { useCallback, useEffect, useRef, useState } from 'react';
import RichBodyEditor from '../RichBodyEditor';
import TemplateLibrary from '../TemplateLibrary';
import RemindersPreviewTabs from './RemindersPreviewTabs';

// Mirrors the fixed 4 keys the shared /suggestions/communities/:id/fields
// route always returns — used as the "everything ticked" default before
// that route's response has loaded, same reasoning as
// GranularInterconnectingEmails.js's own ALL_BASE_FIELD_KEYS.
const ALL_BASE_FIELD_KEYS = ['communityName', 'firstName', 'age', 'gender', 'location'];

const DEFAULT_SUBJECT = 'Still keen to hear from {{pendingNames}}?';
const DEFAULT_BODY = '<p>Hi {{firstName}}!</p>'
  + '<p></p>'
  + "<p>Just a gentle nudge — a little while back we let you know that {{pendingNames}} would love to connect with you, and we haven't heard back yet.</p>"
  + '<p></p>'
  + "<p>No pressure at all if the timing isn't right — but if you've just been busy, it only takes a moment to let us know either way.</p>"
  + '<p></p>'
  + '{{pendingPeopleTeaser}}';
// No sign-off in DEFAULT_BODY — the title/links come from the
// server-side signature (backend/services/emailSignature.js) instead, via
// the "Add my signature" checkbox, so it's never duplicated.

// Third tab alongside Suggestions/Matched Intros: pick a past Suggestions
// round, see who from it still hasn't (fully) responded, and send them a
// follow-up email. Mirrors MatchedIntrosPanel.js's overall shape — same
// 3-prop contract, same TemplateLibrary/RichBodyEditor/excluded-recipient
// patterns — but the audience here comes from a chosen ROUND rather than
// from mutually-accepted clusters.
const RemindersPanel = ({ communityId, communityTitle, authedFetch }) => {
  const [rounds, setRounds] = useState(null);
  const [roundsLoading, setRoundsLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedRoundId, setSelectedRoundId] = useState('');

  const [audience, setAudience] = useState(null);
  const [audienceLoading, setAudienceLoading] = useState(false);
  const [audienceError, setAudienceError] = useState('');
  const [visits, setVisits] = useState([]);

  const [composeOpen, setComposeOpen] = useState(false);
  const [subject, setSubject] = useState(DEFAULT_SUBJECT);
  const [body, setBody] = useState(DEFAULT_BODY);
  const [includeSignature, setIncludeSignature] = useState(true);
  const [sendAsMe, setSendAsMe] = useState(true);
  const [excludedRecipientIds, setExcludedRecipientIds] = useState(() => new Set());
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const [sendResult, setSendResult] = useState(null);

  // Which profile fields show on {{pendingPeopleBlock}}/{{pendingPeopleBlockViewRespond}}
  // cards — same field-picker concept as the Suggestions composer
  // (ComposerPanel.js), reusing its exact fields endpoint since the
  // allowed field list is community-scoped, not feature-scoped.
  const [fields, setFields] = useState({ baseFields: [], communityFields: [] });
  const [includedFields, setIncludedFields] = useState({ base: ALL_BASE_FIELD_KEYS, community: [] });

  // One auto-saved draft per (admin, community) for this tab — mirrors
  // GranularInterconnectingEmails.js's own savedDraft/handleSaveDraft/
  // handleRestoreDraft/handleDiscardDraft, against /admin/reminders/draft
  // (kind:'reminder' on the same EmailDraft model, so it never collides
  // with the Suggestions tab's own draft for the same community).
  const [savedDraft, setSavedDraft] = useState(null);
  const [draftSavedAt, setDraftSavedAt] = useState(null);
  const [savingDraft, setSavingDraft] = useState(false);

  // Live preview plan, fetched from the real backend render path
  // (POST /admin/reminders/preview → buildReminderPlan — the exact same
  // function /reminders/send uses) — see RemindersPreviewTabs.js for why
  // this isn't a client-side mirror. previewRequestIdRef guards against a
  // slower, now-stale request overwriting a newer one, same pattern as
  // GranularInterconnectingEmails.js's own handlePreview.
  const [previewPlan, setPreviewPlan] = useState(null);
  const [previewUpdating, setPreviewUpdating] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const previewRequestIdRef = useRef(0);

  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [expandedHistoryId, setExpandedHistoryId] = useState(null);

  const fetchRounds = useCallback(async () => {
    if (!communityId) return;
    setRoundsLoading(true);
    setError('');
    setSelectedRoundId('');
    setAudience(null);
    setVisits([]);
    setComposeOpen(false);
    setSendResult(null);
    setShowHistory(false);
    setHistory(null);
    try {
      const data = await authedFetch(`/admin/suggestions/rounds?communityId=${communityId}`);
      setRounds(data.rounds || []);
    } catch (err) {
      setError(err.message || 'Failed to load rounds.');
      setRounds(null);
    } finally {
      setRoundsLoading(false);
    }
  }, [communityId, authedFetch]);

  useEffect(() => { fetchRounds(); }, [fetchRounds]);

  const fetchFields = useCallback(async (communityId_) => {
    try {
      const data = await authedFetch(`/admin/suggestions/communities/${communityId_}/fields`);
      setFields({ baseFields: data.baseFields || [], communityFields: data.communityFields || [] });
      // Everything ticked by default — matches the Suggestions composer's
      // own "show everything unless told otherwise" default.
      setIncludedFields({ base: (data.baseFields || []).map((f) => f.key), community: data.communityFields || [] });
    } catch (err) {
      setFields({ baseFields: [], communityFields: [] });
    }
  }, [authedFetch]);

  const fetchDraft = useCallback(async (communityId_) => {
    try {
      const data = await authedFetch(`/admin/reminders/draft?communityId=${communityId_}`);
      setSavedDraft(data.state || null);
      setDraftSavedAt(data.updatedAt || null);
    } catch (err) {
      setSavedDraft(null);
    }
  }, [authedFetch]);

  useEffect(() => {
    if (!communityId) return;
    fetchFields(communityId);
    fetchDraft(communityId);
  }, [communityId, fetchFields, fetchDraft]);

  const fetchAudience = useCallback(async (roundId) => {
    setAudienceLoading(true);
    setAudienceError('');
    setAudience(null);
    setExcludedRecipientIds(new Set());
    setComposeOpen(false);
    setSendResult(null);
    try {
      const data = await authedFetch(`/admin/suggestions/rounds/${roundId}/reminder-audience`);
      setAudience(data);
    } catch (err) {
      setAudienceError(err.message || 'Failed to load reminder audience.');
    } finally {
      setAudienceLoading(false);
    }
  }, [authedFetch]);

  // Passive visit log (RespondPageVisit — see respondPageVisitModel.js) —
  // who actually opened their original /respond link, and how many times.
  // Same read-only endpoint the Suggestions tab's own round-history uses;
  // nothing here affects the connecting flow, purely observational.
  const fetchVisits = useCallback(async (roundId) => {
    try {
      const data = await authedFetch(`/admin/suggestions/rounds/${roundId}/visits`);
      setVisits(data.visits || []);
    } catch (err) {
      setVisits([]);
    }
  }, [authedFetch]);

  const handleSelectRound = (e) => {
    const roundId = e.target.value;
    setSelectedRoundId(roundId);
    if (roundId) {
      fetchAudience(roundId);
      fetchVisits(roundId);
    } else {
      setAudience(null);
      setVisits([]);
    }
  };

  const handlePreview = useCallback(async (body_) => {
    const requestId = ++previewRequestIdRef.current;
    setPreviewUpdating(true);
    setPreviewError('');
    try {
      const plan = await authedFetch('/admin/reminders/preview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body_),
      });
      if (requestId !== previewRequestIdRef.current) return;
      setPreviewPlan(plan);
    } catch (err) {
      if (requestId !== previewRequestIdRef.current) return;
      setPreviewError(err.message || 'Failed to render preview.');
    } finally {
      if (requestId === previewRequestIdRef.current) setPreviewUpdating(false);
    }
  }, [authedFetch]);

  // Live-updating preview: subject/body/excluded-recipient changes reflect
  // within ~500ms with no manual "Preview" click needed, same debounce
  // window as the Suggestions composer's own live preview.
  const previewRequestBody = JSON.stringify({
    roundId: selectedRoundId,
    excludedRecipientUserIds: [...excludedRecipientIds],
    includedFields,
    sharedTemplate: { subject, body },
    includeSignature,
    sendAsMe,
  });
  useEffect(() => {
    if (!composeOpen || !selectedRoundId) {
      setPreviewPlan(null);
      return undefined;
    }
    const timer = setTimeout(() => {
      handlePreview(JSON.parse(previewRequestBody));
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewRequestBody, composeOpen, selectedRoundId]);

  const fetchHistory = useCallback(async () => {
    if (!communityId) return;
    setHistoryLoading(true);
    try {
      const data = await authedFetch(`/admin/reminders/rounds?communityId=${communityId}`);
      setHistory(data.rounds || []);
    } catch (err) {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [communityId, authedFetch]);

  const toggleHistory = () => {
    const next = !showHistory;
    setShowHistory(next);
    if (next && history === null) fetchHistory();
  };

  const toggleExcludedRecipient = (userId) => {
    setExcludedRecipientIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId); else next.add(userId);
      return next;
    });
  };

  const toggleBaseField = (key) => {
    setIncludedFields((prev) => {
      const has = prev.base.includes(key);
      return { ...prev, base: has ? prev.base.filter((f) => f !== key) : [...prev.base, key] };
    });
  };

  const toggleCommunityField = (name) => {
    setIncludedFields((prev) => {
      const has = prev.community.includes(name);
      return { ...prev, community: has ? prev.community.filter((f) => f !== name) : [...prev.community, name] };
    });
  };

  const handleSend = async () => {
    setSending(true);
    setSendError('');
    setSendResult(null);
    try {
      const data = await authedFetch('/admin/reminders/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          roundId: selectedRoundId,
          excludedRecipientUserIds: [...excludedRecipientIds],
          includedFields,
          sharedTemplate: { subject, body },
          includeSignature,
          sendAsMe,
          label: '',
        }),
      });
      setSendResult(data);
      fetchAudience(selectedRoundId);
      // A sent round is no longer a "draft" of anything — clear it out so a
      // stale banner never offers to restore an already-sent setup.
      authedFetch(`/admin/reminders/draft?communityId=${communityId}`, { method: 'DELETE' }).catch(() => {});
    } catch (err) {
      setSendError(err.message || 'Failed to send.');
    } finally {
      setSending(false);
    }
  };

  const handleSaveDraft = async () => {
    setSavingDraft(true);
    try {
      await authedFetch('/admin/reminders/draft', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          communityId,
          state: {
            selectedRoundId,
            excludedRecipientUserIds: [...excludedRecipientIds],
            includedFields,
            sharedTemplate: { subject, body },
          },
        }),
      });
      setSavedDraft(null); // this IS now the current on-screen state — nothing to offer restoring
      setDraftSavedAt(new Date().toISOString());
    } catch (err) {
      setSendError(err.message || 'Failed to save draft.');
    } finally {
      setSavingDraft(false);
    }
  };

  const handleRestoreDraft = () => {
    if (!savedDraft) return;
    setIncludedFields(savedDraft.includedFields || { base: ALL_BASE_FIELD_KEYS, community: fields.communityFields });
    setSubject(savedDraft.sharedTemplate?.subject || DEFAULT_SUBJECT);
    setBody(savedDraft.sharedTemplate?.body || DEFAULT_BODY);
    setSavedDraft(null);
    if (savedDraft.selectedRoundId) {
      setSelectedRoundId(savedDraft.selectedRoundId);
      fetchVisits(savedDraft.selectedRoundId);
      fetchAudience(savedDraft.selectedRoundId).then(() => {
        setExcludedRecipientIds(new Set(savedDraft.excludedRecipientUserIds || []));
        setComposeOpen(true);
      });
    }
  };

  const handleDiscardDraft = async () => {
    setSavedDraft(null);
    setDraftSavedAt(null);
    try {
      await authedFetch(`/admin/reminders/draft?communityId=${communityId}`, { method: 'DELETE' });
    } catch (err) {
      // Non-critical — the banner's already gone from the admin's point of view either way.
    }
  };

  if (roundsLoading) return <div className="as-loading">Loading past rounds…</div>;
  if (error) return <div className="as-error">{error}</div>;
  if (!rounds) return null;

  const recipients = audience?.recipients || [];
  const sendCount = recipients.filter((r) => !excludedRecipientIds.has(r.userId)).length;

  // Email delivery status (sent/failed/dry_run/suppressed) for the ORIGINAL
  // suggestion round's send — sourced from the same rounds list already
  // fetched for the round picker (each round's own `recipients` carries
  // per-address status), keyed by email since that's the only shared key
  // between EmailMessage rows and this audience's recipients.
  const selectedRound = rounds.find((r) => r._id === selectedRoundId);
  const emailStatusByAddress = new Map((selectedRound?.recipients || []).map((r) => [r.email, r.status]));
  const visitByUserId = new Map(visits.map((v) => [v.recipientUserId, v]));
  const formatDateTime = (d) => new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });

  return (
    <div className="as-matched-intros">
      <h2 className="as-section-title">{communityTitle} — Reminders</h2>

      {savedDraft && (
        <div className="as-draft-banner">
          <span>💾 You have a saved draft for this community.</span>
          <button type="button" className="as-filter-tab" onClick={handleRestoreDraft}>Restore</button>
          <button type="button" className="as-filter-tab" onClick={handleDiscardDraft}>Discard</button>
        </div>
      )}

      {rounds.length === 0 && (
        <div className="as-empty">No past Suggestions rounds for this community yet — send a Suggestions round first.</div>
      )}

      {rounds.length > 0 && (
        <div className="as-email-field">
          <label>Pick a past Suggestions round to remind</label>
          <select className="as-filter-tab" value={selectedRoundId} onChange={handleSelectRound}>
            <option value="">— Select a round —</option>
            {rounds.map((r) => (
              <option key={r._id} value={r._id}>
                {(r.label ? `${r.label} — ` : '')}{new Date(r.createdAt).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })} ({r.recipients.length} emailed)
              </option>
            ))}
          </select>
        </div>
      )}

      {audienceLoading && <div className="as-loading">Checking who still hasn't responded…</div>}
      {audienceError && <div className="as-error">{audienceError}</div>}

      {audience && recipients.length === 0 && (
        <div className="as-empty" style={{ marginTop: '1rem' }}>Everyone from this round has already responded — nothing to remind.</div>
      )}

      {audience && recipients.length > 0 && (
        <>
          <div className="as-card-meta" style={{ margin: '1rem 0 0.5rem' }}>
            {recipients.length} people still have at least one pending response from this round:
          </div>
          <table className="as-ab-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Still pending</th>
                <th>Already answered</th>
                <th>Original email</th>
                <th>Opened /respond link</th>
              </tr>
            </thead>
            <tbody>
              {recipients.map((r) => {
                const emailStatus = emailStatusByAddress.get(r.email);
                const visit = visitByUserId.get(r.userId);
                return (
                  <tr key={r.userId}>
                    <td>{r.firstName || r.userId}</td>
                    <td>{r.pendingPartners.map((p) => p.firstName || p.userId).join(', ')}</td>
                    <td>{r.answeredPartners.length > 0 ? r.answeredPartners.map((p) => p.firstName || p.userId).join(', ') : '—'}</td>
                    <td>
                      {emailStatus === 'sent' && <span style={{ color: '#15803d' }}>Sent</span>}
                      {emailStatus === 'failed' && <span style={{ color: '#b91c1c' }}>Failed</span>}
                      {emailStatus === 'suppressed' && <span style={{ color: '#b45309' }}>Suppressed (unsubscribed)</span>}
                      {emailStatus === 'dry_run' && <span style={{ color: '#64748b' }}>Dry run</span>}
                      {!emailStatus && '—'}
                    </td>
                    <td>
                      {visit
                        ? `Yes — ${visit.visitCount}× (last ${formatDateTime(visit.lastVisitedAt)})`
                        : <span style={{ color: '#64748b' }}>Not yet</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div className="as-activity-toolbar" style={{ marginTop: '1.5rem' }}>
            <button
              type="button"
              className="as-btn as-btn--approve"
              onClick={() => setComposeOpen((v) => !v)}
            >
              Compose reminder email ({recipients.length} eligible)
            </button>
          </div>

          {composeOpen && (
            <div className="as-suggestion-body-preview" style={{ marginTop: '1rem', padding: '1rem' }}>
              {recipients.length > 1 && (
                <div style={{ marginBottom: '1rem' }}>
                  <div className="as-card-meta" style={{ marginBottom: '0.4rem' }}>
                    Who actually gets a reminder — click a name to move it.
                  </div>
                  <table className="as-ab-table">
                    <thead>
                      <tr>
                        <th>Being sent</th>
                        <th>Not being sent</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td style={{ verticalAlign: 'top' }}>
                          {recipients.filter((r) => !excludedRecipientIds.has(r.userId)).map((r) => (
                            <button
                              key={r.userId}
                              type="button"
                              className="as-filter-tab"
                              style={{ margin: '0.2rem' }}
                              onClick={() => toggleExcludedRecipient(r.userId)}
                              title="Click to stop reminding this person"
                            >
                              {r.firstName || '(no name)'}
                            </button>
                          ))}
                        </td>
                        <td style={{ verticalAlign: 'top' }}>
                          {recipients.filter((r) => excludedRecipientIds.has(r.userId)).map((r) => (
                            <button
                              key={r.userId}
                              type="button"
                              className="as-filter-tab"
                              style={{ margin: '0.2rem' }}
                              onClick={() => toggleExcludedRecipient(r.userId)}
                              title="Click to remind this person again"
                            >
                              {r.firstName || '(no name)'}
                            </button>
                          ))}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}

              <div className="as-suggestion-field-picker">
                <span className="as-card-meta">Include in {'{{pendingPeopleBlock}}'}/{'{{pendingPeopleBlockViewRespond}}'} cards:</span>
                <div className="as-suggestion-field-checks">
                  {fields.baseFields.map((f) => (
                    <label key={f.key} className="as-suggestion-checkbox-label">
                      <input type="checkbox" checked={includedFields.base.includes(f.key)} onChange={() => toggleBaseField(f.key)} />
                      {f.label}
                    </label>
                  ))}
                  {fields.communityFields.map((name) => (
                    <label key={name} className="as-suggestion-checkbox-label">
                      <input type="checkbox" checked={includedFields.community.includes(name)} onChange={() => toggleCommunityField(name)} />
                      {name}
                    </label>
                  ))}
                </div>
              </div>

              <TemplateLibrary authedFetch={authedFetch} subject={subject} body={body} onLoad={({ subject: s, body: b }) => { setSubject(s); setBody(b); }} />

              <div className="as-email-field" style={{ marginTop: '0.75rem' }}>
                <label>Subject</label>
                <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)} />
              </div>
              <div className="as-activity-toolbar" style={{ marginTop: '0.75rem' }}>
                <label className="as-suggestion-checkbox-label">
                  <input type="checkbox" checked={sendAsMe} onChange={(e) => setSendAsMe(e.target.checked)} />
                  Send as me (From: "James at {communityTitle}")
                </label>
                <label className="as-suggestion-checkbox-label">
                  <input type="checkbox" checked={includeSignature} onChange={(e) => setIncludeSignature(e.target.checked)} />
                  Add my signature
                </label>
              </div>
              <div className="as-email-field" style={{ marginTop: '0.75rem' }}>
                <label>Body — placeholders, replaced per-recipient (only their still-PENDING partners, never ones they've already answered on):</label>
                <ul style={{ margin: '0.3rem 0 0.5rem', paddingLeft: '1.2rem' }} className="as-card-meta">
                  <li><code>{'{{firstName}}'}</code> — this recipient's own first name</li>
                  <li><code>{'{{communityName}}'}</code> — this community's name</li>
                  <li><code>{'{{pendingNames}}'}</code> — only the partners still pending (e.g. "Alex and Sam")</li>
                  <li><code>{'{{pendingCount}}'}</code> — how many are still pending, as a plain number</li>
                  <li><code>{'{{pendingPeopleTeaser}}'}</code> — a low-key avatar row + CTA (recommended default)</li>
                  <li><code>{'{{pendingPeopleBlock}}'}</code> — full profile cards for the still-pending partners, one-click Connect button</li>
                  <li><code>{'{{pendingPeopleBlockViewRespond}}'}</code> — same cards, but each links to the /respond page instead of one-click connecting</li>
                  <li><code>{'{{pendingPeopleBlock_<label>}}'}</code> — same cards with your own button wording, e.g. <code>{'{{pendingPeopleBlock_Say hi}}'}</code></li>
                  <li><code>{'{{pendingPeopleTeaser_<label>}}'}</code> — the teaser with your own CTA wording, e.g. <code>{'{{pendingPeopleTeaser_Take a look}}'}</code></li>
                  <li><code>{'{{respondButton}}'}</code> / <code>{'{{connectPromptButton}}'}</code> — the same round-level /respond link as the original email, two default wordings</li>
                  <li><code>{'{{respondButton_<label>}}'}</code> — the same link with your own button wording, e.g. <code>{'{{respondButton_Let us know →}}'}</code></li>
                  <li><code>{'{{respondUrl}}'}</code> — the raw /respond link, for hyperlinking your own text</li>
                </ul>
                <RichBodyEditor
                  value={body}
                  onChange={setBody}
                  placeholder="Hi {{firstName}}, ... {{pendingPeopleTeaser}}"
                  showRespondLinkButton
                />
              </div>

              <div className="as-email-preview" style={{ marginTop: '1rem' }}>
                <div className="as-card-meta">
                  Preview
                  <button type="button" className="as-filter-tab" style={{ marginLeft: '0.75rem' }} onClick={() => setComposeOpen(false)} disabled={sending}>
                    Cancel
                  </button>
                  <button type="button" className="as-btn as-btn--approve" style={{ marginLeft: '0.75rem' }} onClick={handleSend} disabled={sending || sendCount === 0}>
                    {sending ? 'Sending…' : `📤 Send (${sendCount})`}
                  </button>
                  <button className="as-filter-tab" style={{ marginLeft: '0.75rem' }} onClick={handleSaveDraft} disabled={savingDraft}>
                    {savingDraft ? 'Saving…' : '💾 Save Draft'}
                  </button>
                  {draftSavedAt && <span className="as-card-meta" style={{ marginLeft: '0.5rem' }}>Draft saved</span>}
                </div>

                {sendError && <div className="as-error" style={{ marginTop: '0.5rem' }}>{sendError}</div>}
                {previewError && <div className="as-error" style={{ marginTop: '0.5rem' }}>{previewError}</div>}
                {previewUpdating && <div className="as-loading" style={{ marginTop: '0.5rem' }}>Updating preview…</div>}

                {previewPlan?.warnings?.length > 0 && (
                  <ul className="as-suggestion-warnings">
                    {previewPlan.warnings.map((w, i) => <li key={i}>⚠ {w}</li>)}
                  </ul>
                )}

                {previewPlan?.community?.senderAddress && (
                  <div className="as-email-preview-from">From: {previewPlan.community.senderName ? `${previewPlan.community.senderName} <${previewPlan.community.senderAddress}>` : previewPlan.community.senderAddress}</div>
                )}

                <RemindersPreviewTabs previewPlan={previewPlan} />
              </div>
            </div>
          )}
        </>
      )}

      {sendResult && (
        <div className="as-card-meta" style={{ marginTop: '1rem' }}>
          Sent {sendResult.sent || 0}, failed {sendResult.failed || 0}, suppressed {sendResult.suppressed || 0}{sendResult.dryRun ? ' (dry run)' : ''}.
          {sendResult.failed > 0 && sendResult.firstError && (
            <div className="as-error" style={{ marginTop: '0.5rem' }}>Why it failed: {sendResult.firstError}</div>
          )}
        </div>
      )}

      <div className="as-activity-toolbar" style={{ marginTop: '1.5rem' }}>
        <button type="button" className="as-filter-tab" onClick={toggleHistory}>
          {showHistory ? '▾' : '▸'} History
        </button>
      </div>

      {showHistory && (
        <div>
          {historyLoading && <div className="as-loading">Loading…</div>}
          {history && history.length === 0 && <div className="as-empty">No reminders sent yet for this community.</div>}
          {history && history.length > 0 && (
            <table className="as-ab-table">
              <thead>
                <tr>
                  <th>Reminder for</th>
                  <th>Sent</th>
                  <th>By</th>
                  <th>Recipients</th>
                </tr>
              </thead>
              <tbody>
                {history.map((round) => (
                  <React.Fragment key={round._id}>
                    <tr style={{ cursor: 'pointer' }} onClick={() => setExpandedHistoryId(expandedHistoryId === round._id ? null : round._id)}>
                      <td>{round.sourceRoundLabel || '(untitled round)'}</td>
                      <td>{new Date(round.createdAt).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                      <td>{round.createdBy}</td>
                      <td>{round.recipients.length}</td>
                    </tr>
                    {expandedHistoryId === round._id && (
                      <tr>
                        <td colSpan={4}>
                          <ul style={{ margin: '0.4rem 0', paddingLeft: '1.2rem' }}>
                            {round.recipients.map((r) => (
                              <li key={r._id} className="as-card-meta">{r.email} — {r.status}</li>
                            ))}
                          </ul>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

export default RemindersPanel;
